import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useSelector } from "react-redux";
import NunitoText from "../general/NunitoText";
import ReusableAutocompleteWithID from "../general/ReusableAutocompleteWithID";
import ReusableButton from "../general/ReusableButton";
import ReusableDatePicker from "../general/ReusableDatePicker";
import { showWarningSwal } from "../../functions/alert";
import { gradeOptions as defaultGradeOptions } from "../../functions/general";
import {
  buildLanguagesQuery,
  extractAssignmentFilterOptions,
  toAssignmentSelectOption,
} from "../../functions/assignmentFilterOptions";
import { getAssignmentStoreFilterOptions, getCompetitions } from "../../functions/getData";
import { addAssignment } from "../../functions/postData";

const rowContainerStyle = { paddingInline: 34, marginBottom: 24 };
const buttonsContainerStyle = { paddingInline: 100, marginBlock: 30 };
const boxStyle = { width: 1100, maxHeight: "90%" };

const toDateString = (timestamp) => {
  if (!timestamp) return null;
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function AddAssigneeModal({
  open = true,
  setLoading,
  setOnAdd,
  afterAdd,
}) {
  const countryStoreOptions = useSelector((state) => state.countryOptions || []);

  const [competitionOptions, setCompetitionOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [gradeFilterOptions, setGradeFilterOptions] = useState([]);

  const [competition, setCompetition] = useState("");
  const [country, setCountry] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [grades, setGrades] = useState([]);
  // TODO: re-enable when the backend exposes the users payload for assignments.
  // const [users, setUsers] = useState([]);
  // const [userOptions, setUserOptions] = useState([]);
  const [dateRange, setDateRange] = useState([]);

  // Load competitions only while this modal is open (same lazy idea as
  // EnhancedFilter’s onFocus). Parent should pass `open` and/or mount this
  // component only when the dialog is visible so DevTools shows the request
  // when you click “Add Assignee”, not on an earlier hidden mount.
  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await getAssignmentStoreFilterOptions(
          "?get_filter=competition"
        );
        if (cancelled) return;
        let list = extractAssignmentFilterOptions(res, [
          "competition",
          "competitions",
        ]);
        if (!list.length) {
          const fb = await getCompetitions("", undefined).catch(() => null);
          if (cancelled) return;
          if (Array.isArray(fb)) list = fb;
          else
            list = extractAssignmentFilterOptions(fb, [
              "competition",
              "competitions",
            ]);
        }
        setCompetitionOptions(
          list
            .map(toAssignmentSelectOption)
            .filter((o) => o.id != null && o.id !== "" && String(o.option).trim() !== "")
        );
      } catch {
        if (cancelled) return;
        try {
          const fb = await getCompetitions("", undefined);
          if (cancelled) return;
          const list = Array.isArray(fb)
            ? fb
            : extractAssignmentFilterOptions(fb, [
                "competition",
                "competitions",
              ]);
          setCompetitionOptions(
            list
              .map(toAssignmentSelectOption)
              .filter(
                (o) =>
                  o.id != null && o.id !== "" && String(o.option).trim() !== ""
              )
          );
        } catch {
          setCompetitionOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // When the dialog closes while the component stays mounted, clear selections
  // so the next open does not reuse stale IDs or fire cascade calls wrongly.
  useEffect(() => {
    if (open) return;
    setCompetition("");
    setCountry("");
    setOrganizations([]);
    setLanguages([]);
    setGrades([]);
    setDateRange([]);
    setCompetitionOptions([]);
    setCountryOptions([]);
    setOrganizationOptions([]);
    setLanguageOptions([]);
    setGradeFilterOptions([]);
  }, [open]);

  // On competition change -> fetch countries via store-filter-options
  useEffect(() => {
    if (!competition) {
      setCountryOptions([]);
      setCountry("");
      return;
    }
    setCountry("");
    setOrganizations([]);
    setLanguages([]);
    setGrades([]);
    getAssignmentStoreFilterOptions(
      `?get_filter=country&competition=${competition}`
    )
      .then((res) => {
        const list = extractAssignmentFilterOptions(res, ["country", "countries"]);
        if (list.length) {
          setCountryOptions(list.map(toAssignmentSelectOption));
        } else {
          // Fallback to global country list if backend ignores the filter param.
          setCountryOptions(
            countryStoreOptions.map((c) => ({ id: c.id, option: c.display_name }))
          );
        }
      })
      .catch(() => {
        setCountryOptions(
          countryStoreOptions.map((c) => ({ id: c.id, option: c.display_name }))
        );
      });
  }, [competition, countryStoreOptions]);

  // On country change -> fetch organizations
  useEffect(() => {
    if (!competition || !country) {
      setOrganizationOptions([]);
      setOrganizations([]);
      return;
    }
    setOrganizations([]);
    setLanguages([]);
    setGrades([]);
    getAssignmentStoreFilterOptions(
      `?get_filter=organization&competition=${competition}&country=${country}`
    )
      .then((res) => {
        const list = extractAssignmentFilterOptions(res, [
          "organization",
          "organizations",
        ]);
        setOrganizationOptions(list.map(toAssignmentSelectOption));
      })
      .catch(() => setOrganizationOptions([]));
  }, [competition, country]);

  // On organization change -> fetch languages
  useEffect(() => {
    if (!competition || !country || organizations.length === 0) {
      setLanguageOptions([]);
      setLanguages([]);
      setGrades([]);
      return;
    }
    // Postman example uses a single `organization=` param; languages are scoped
    // by the chosen competition/country, so the first selected org is enough.
    const orgId = organizations[0];
    getAssignmentStoreFilterOptions(
      `?get_filter=languages&competition=${competition}&country=${country}&organization=${orgId}`
    )
      .then((res) => {
        const list = extractAssignmentFilterOptions(res, [
          "languages",
          "language",
        ]);
        setLanguageOptions(list.map(toAssignmentSelectOption));
      })
      .catch(() => setLanguageOptions([]));
  }, [competition, country, organizations]);

  // On languages change -> fetch grades scoped to the full cascade.
  // The backend expects each language id as a repeated `languages[]=` param.
  useEffect(() => {
    if (
      !competition ||
      !country ||
      organizations.length === 0 ||
      languages.length === 0
    ) {
      setGradeFilterOptions([]);
      setGrades([]);
      return;
    }
    const orgId = organizations[0];
    const langQuery = buildLanguagesQuery(languages);
    getAssignmentStoreFilterOptions(
      `?get_filter=grades&competition=${competition}&country=${country}&organization=${orgId}&${langQuery}`
    )
      .then((res) => {
        const list = extractAssignmentFilterOptions(res, ["grades", "grade"]);
        if (list.length) {
          setGradeFilterOptions(list.map(toAssignmentSelectOption));
        } else {
          // Fallback to the static grade catalogue if backend ignores the filter.
          setGradeFilterOptions(
            defaultGradeOptions.map((g) => ({ id: g.id, option: g.name }))
          );
        }
      })
      .catch(() => {
        setGradeFilterOptions(
          defaultGradeOptions.map((g) => ({ id: g.id, option: g.name }))
        );
      });
    // Wipe any previously picked grades since the catalogue just changed.
    setGrades([]);
  }, [competition, country, organizations, languages]);

  const onSubmit = () => {
    let warning = "";
    if (!competition) warning += "Please select a competition.<br>";
    if (!country) warning += "Please select a country.<br>";
    if (!organizations.length)
      warning += "Please select at least one organization.<br>";
    if (!languages.length)
      warning += "Please select at least one language.<br>";
    if (!Array.isArray(dateRange) || dateRange.length < 2)
      warning += "Please pick both the From and To dates.<br>";

    if (warning) return showWarningSwal(warning);

    // Backend example only models a single organization_id; if multiple are
    // selected we issue one POST per organization. This keeps the UI flexible
    // and works for the common single-org case.
    // The range picker returns the two dates as a sorted array of timestamps.
    const sorted = [...dateRange].sort((a, b) => a - b);
    const fromDate = toDateString(sorted[0]);
    const toDate = toDateString(sorted[sorted.length - 1]);
    const payloads = organizations.map((orgId) => ({
      competition_id: competition,
      country_id: country,
      organization_id: orgId,
      languages,
      grades,
      from_date: fromDate,
      to_date: toDate,
    }));

    setLoading(true);
    Promise.all(payloads.map((p) => addAssignment(p)))
      .then((results) => {
        setLoading(false);
        // Failures already show a warning popup from postMethod2.
        const failed = results.filter(
          (r) => ![200, 201].includes(Number(r?.status))
        );
        const last = results[results.length - 1];
        afterAdd(() => setOnAdd(false), {
          status: failed.length ? Number(failed[0]?.status) : 200,
          message: failed.length
            ? failed[0]?.data?.message
            : last?.data?.message,
          anySucceeded: failed.length < results.length,
        });
      })
      .catch(() => setLoading(false));
  };

  return (
    <Box className="popUpModal horizontalScrollable" style={boxStyle}>
      <NunitoText
        value="ADD ASSIGNEE"
        fontSize={30}
        fontWeight={600}
        color="#5E75C3"
        marginTop={20}
      />

      <Grid container justifyContent="space-between" style={rowContainerStyle}>
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Competition*"
          state={competition}
          setState={setCompetition}
          required
          options={competitionOptions}
        />
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Country*"
          state={country}
          setState={setCountry}
          required
          options={countryOptions}
          grayedOut={!competition}
          readOnly={!competition}
        />
      </Grid>

      <Grid container justifyContent="space-between" style={rowContainerStyle}>
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Organization*"
          state={organizations}
          setState={setOrganizations}
          required
          multiple
          options={organizationOptions}
          grayedOut={!country}
          readOnly={!country}
        />
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Languages*"
          state={languages}
          setState={setLanguages}
          required
          multiple
          options={languageOptions}
          grayedOut={!organizations.length}
          readOnly={!organizations.length}
        />
      </Grid>

      <Grid container justifyContent="space-between" style={rowContainerStyle}>
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Grades"
          state={grades}
          setState={setGrades}
          multiple
          options={gradeFilterOptions}
          grayedOut={!languages.length}
          readOnly={!languages.length}
        />
        {/*
          TODO: Users selector - re-enable when the backend exposes
          /translations/assignments/store-filter-options?get_filter=users and
          the POST /translations/assignments payload accepts `users[]`.

          <ReusableAutocompleteWithID
            type="withLabel"
            width={500}
            placeholder="Users (default All)"
            state={users}
            setState={setUsers}
            multiple
            options={userOptions}
            grayedOut={!languages.length}
            readOnly={!languages.length}
          />
        */}
      </Grid>

      <Grid container style={rowContainerStyle}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <span
            style={{
              color: "#000",
              fontSize: 16,
              fontFamily: "Nunito",
              fontWeight: 600,
              fontStyle: "italic",
              marginBottom: 4,
            }}
          >
            From - To*
          </span>
          <ReusableDatePicker
            width={500}
            multiple
            range
            allowEmpty
            state={dateRange}
            setState={setDateRange}
            required={dateRange.length < 2}
          />
        </div>
      </Grid>

      <Grid container justifyContent="space-evenly" style={buttonsContainerStyle}>
        <ReusableButton
          text="Cancel"
          bgColor="#8D8D8D"
          fontSize={20}
          height={56}
          width={260}
          onClick={() => setOnAdd(false)}
        />
        <ReusableButton
          text="Submit"
          bgColor="#5E75C3"
          fontSize={20}
          height={56}
          width={260}
          onClick={onSubmit}
        />
      </Grid>
    </Box>
  );
}
